// import React, { useState, useEffect, useRef } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { Button } from '../components/ui/button';
// import { useAuth } from '../contexts/AuthContext';
// import { useTheme } from '../contexts/ThemeContext';
// import {
//   Cpu, Zap, ShieldCheck, Users, TrendingUp, BarChart3,
//   ArrowRight, Check, Globe, Activity, Sparkles,
//   Brain, Lock, Layers, ChevronRight, Star, Cpu as CpuIcon
// } from 'lucide-react';
// import HeroOrb from '../components/HeroOrb';
// import Footer from '../components/Footer';
// import AppHeader from '../components/AppHeader';

// /* ── Animated counter hook ─────────────────────────── */
// function useCounter(target, duration = 1800, start = false) {
//   const [value, setValue] = useState(0);
//   useEffect(() => {
//     if (!start) return;
//     let startTime = null;
//     const step = (timestamp) => {
//       if (!startTime) startTime = timestamp;
//       const progress = Math.min((timestamp - startTime) / duration, 1);
//       const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
//       setValue(Math.floor(eased * target));
//       if (progress < 1) requestAnimationFrame(step);
//     };
//     requestAnimationFrame(step);
//   }, [target, duration, start]);
//   return value;
// }

// /* ── Intersection Observer hook ─────────────────────── */
// function useInView(threshold = 0.2) {
//   const ref = useRef(null);
//   const [inView, setInView] = useState(false);
//   useEffect(() => {
//     const obs = new IntersectionObserver(
//       ([entry]) => { if (entry.isIntersecting) setInView(true); },
//       { threshold }
//     );
//     if (ref.current) obs.observe(ref.current);
//     return () => obs.disconnect();
//   }, [threshold]);
//   return [ref, inView];
// }

// const AGENTS = [
//   { name: 'Banking Risk & Compliance', icon: ShieldCheck, category: 'Banking & Finance', accentColor: '#3B82F6' },
//   { name: 'Customer Support', icon: Users, category: 'Customer Experience', accentColor: '#8B5CF6' },
//   { name: 'Sales', icon: TrendingUp, category: 'Sales', accentColor: '#10B981' },
//   { name: 'HR Resume Screening', icon: Users, category: 'Human Resources', accentColor: '#EC4899' },
//   { name: 'BI Data Storyteller', icon: BarChart3, category: 'Analytics', accentColor: '#F59E0B' },
//   { name: 'Clinical Care', icon: Activity, category: 'Healthcare', accentColor: '#14B8A6' },
// ];

// const FEATURES = [
//   {
//     icon: Brain,
//     title: 'ReAct Intelligence',
//     desc: 'Agents reason step-by-step, call tools, observe results — delivering accurate, auditable answers every time.',
//     accentColor: '#F87217',
//   },
//   {
//     icon: Lock,
//     title: 'Enterprise Security',
//     desc: 'SOC2, GDPR, HIPAA-ready. AES-256 encryption at rest and in transit. Your data never leaves your environment.',
//     accentColor: '#3B82F6',
//   },
//   {
//     icon: Layers,
//     title: 'Plug & Play Integration',
//     desc: 'Connect to Salesforce, Dynamics 365, Azure, Twilio, and more with zero custom code.',
//     accentColor: '#8B5CF6',
//   },
//   {
//     icon: Zap,
//     title: 'Credit-Based Scale',
//     desc: 'Pay only for what you use. Start free, scale seamlessly from solo to enterprise-grade workloads.',
//     accentColor: '#10B981',
//   },
// ];

// const plans = [
//   {
//     name: 'Free',
//     price: '$0',
//     period: '/month',
//     credits: '100 credits',
//     features: ['Access to Free-tier agents', 'Basic support', 'Community access', '20 MB Knowledge Base Storage'],
//     popular: false,
//   },
//   {
//     name: 'Pro',
//     price: '$49',
//     period: '/month',
//     credits: '5,000 credits',
//     features: ['All Pro agents', 'Priority support', 'API access', 'Custom integrations', '1 GB Knowledge Base Storage'],
//     popular: true,
//   },
//   {
//     name: 'Enterprise',
//     price: '$299',
//     period: '/month',
//     credits: '50,000+ credits',
//     features: ['All agents', 'Dedicated support', 'SLA guarantee', 'On-premise option', '2 GB Knowledge Base Storage'],
//     popular: false,
//   },
//   {
//     name: 'Enterprise Pro',
//     price: '$499',
//     period: '/month',
//     credits: '80,000 credits',
//     features: ['Access to all agents', 'Dedicated account manager + onboarding', 'Advanced analytics', '5GB Knowledge Base Storage'],
//     popular: false,
//   },
// ];

// /* ── Feature Card ───────────────────────────────────── */
// function FeatureCard({ feature, index, isDark }) {
//   const [ref, inView] = useInView(0.15);
//   const cardBg = isDark ? '#0f1729' : '#fff';
//   const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
//   const cardShadow = isDark ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.04)';
//   return (
//     <div
//       ref={ref}
//       className="group relative p-7 rounded-2xl border transition-all duration-300 cursor-default"
//       style={{
//         opacity: inView ? 1 : 0,
//         transform: inView ? 'translateY(0)' : 'translateY(28px)',
//         transition: `opacity 0.5s ease ${index * 0.1}s, transform 0.5s ease ${index * 0.1}s, box-shadow 0.25s ease, border-color 0.25s ease`,
//         background: cardBg,
//         border: `1px solid ${cardBorder}`,
//         boxShadow: cardShadow,
//       }}
//       onMouseEnter={e => {
//         e.currentTarget.style.borderColor = feature.accentColor + '55';
//         e.currentTarget.style.boxShadow = `0 12px 40px ${feature.accentColor}18, 0 4px 12px rgba(0,0,0,0.06)`;
//         e.currentTarget.style.transform = 'translateY(-4px)';
//       }}
//       onMouseLeave={e => {
//         e.currentTarget.style.borderColor = cardBorder;
//         e.currentTarget.style.boxShadow = cardShadow;
//         e.currentTarget.style.transform = 'translateY(0)';
//       }}
//     >
//       <div
//         className="absolute top-0 left-6 right-6 h-[2px] rounded-b-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
//         style={{ background: `linear-gradient(90deg,${feature.accentColor},${feature.accentColor}88)` }}
//       />
//       <div
//         className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110"
//         style={{ background: feature.accentColor + '12', border: `1px solid ${feature.accentColor}22` }}
//       >
//         <feature.icon className="w-5 h-5" style={{ color: feature.accentColor }} />
//       </div>
//       <h3 className="font-bold mb-2.5 text-base" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>{feature.title}</h3>
//       <p className="text-sm leading-relaxed" style={{ color: isDark ? '#8b949e' : '#64748b' }}>{feature.desc}</p>
//     </div>
//   );
// }

// /* ── Agent Card ─────────────────────────────────────── */
// function AgentCard({ agent, index, onClick, isDark }) {
//   const [ref, inView] = useInView(0.1);
//   const cardBg = isDark ? '#0f1729' : '#fff';
//   const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(226,232,240,1)';
//   return (
//     <div
//       ref={ref}
//       className="group relative border rounded-2xl p-5 flex items-center gap-4 cursor-pointer"
//       onClick={onClick}
//       style={{
//         background: cardBg,
//         border: `1px solid ${cardBorder}`,
//         opacity: inView ? 1 : 0,
//         transform: inView ? 'translateY(0)' : 'translateY(20px)',
//         transition: `opacity 0.45s ease ${index * 0.08}s, transform 0.45s ease ${index * 0.08}s, box-shadow 0.25s ease, border-color 0.25s ease`,
//         boxShadow: isDark ? '0 1px 4px rgba(0,0,0,0.4)' : '0 1px 4px rgba(0,0,0,0.05)',
//       }}
//       onMouseEnter={e => {
//         e.currentTarget.style.borderColor = '#F8721733';
//         e.currentTarget.style.boxShadow = '0 8px 28px rgba(248,114,23,0.12), 0 2px 8px rgba(0,0,0,0.05)';
//         e.currentTarget.style.transform = 'translateY(-3px)';
//       }}
//       onMouseLeave={e => {
//         e.currentTarget.style.borderColor = cardBorder;
//         e.currentTarget.style.boxShadow = isDark ? '0 1px 4px rgba(0,0,0,0.4)' : '0 1px 4px rgba(0,0,0,0.05)';
//         e.currentTarget.style.transform = 'translateY(0)';
//       }}
//     >
//       <div
//         className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
//         style={{ background: agent.accentColor + '14', border: `1px solid ${agent.accentColor}22` }}
//       >
//         <agent.icon className="w-5 h-5" style={{ color: agent.accentColor }} />
//       </div>
//       <div className="min-w-0 flex-1">
//         <p className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color: isDark ? '#586069' : '#94a3b8' }}>{agent.category}</p>
//         <h3 className="font-semibold text-sm truncate group-hover:text-[#F87217] transition-colors duration-200" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>{agent.name}</h3>
//       </div>
//       <ArrowRight
//         className="w-4 h-4 flex-shrink-0 transition-all duration-200 group-hover:translate-x-1"
//         style={{ color: '#F87217', opacity: 0.5 }}
//       />
//     </div>
//   );
// }

// /* ── Pricing Card ───────────────────────────────────── */
// function PricingCard({ plan, index, onGetStarted }) {
//   const [ref, inView] = useInView(0.1);
//   return (
//     <div
//       ref={ref}
//       className="relative rounded-2xl p-5 flex flex-col"
//       style={{
//         opacity: inView ? 1 : 0,
//         transform: inView ? 'translateY(0)' : 'translateY(24px)',
//         transition: `opacity 0.5s ease ${index * 0.1}s, transform 0.5s ease ${index * 0.1}s`,
//         background: plan.popular
//           ? 'linear-gradient(145deg,#F87217 0%,#FF9A4E 100%)'
//           : '#fff',
//         border: plan.popular ? 'none' : '1px solid rgba(0,0,0,0.07)',
//         boxShadow: plan.popular
//           ? '0 24px 64px rgba(248,114,23,0.35), 0 8px 24px rgba(248,114,23,0.2)'
//           : '0 2px 8px rgba(0,0,0,0.04)',
//       }}
//     >
//       {plan.popular && (
//         <div
//           className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-5 py-1.5 text-xs font-bold rounded-full flex items-center gap-1.5"
//           style={{ background: '#0d1a3a', color: '#fff' }}
//         >
//           <Star className="w-3 h-3 fill-[#F87217] stroke-none" />
//           MOST POPULAR
//         </div>
//       )}
//       <h3
//         className="text-sm font-bold mb-1"
//         style={{ color: plan.popular ? 'rgba(255,255,255,0.9)' : '#0d1a3a' }}
//       >
//         {plan.name}
//       </h3>
//       <div className="flex items-end gap-1 mt-2 mb-1.5">
//         <span
//           className="text-2xl font-extrabold tracking-tight"
//           style={{ color: plan.popular ? '#fff' : '#0d1a3a' }}
//         >
//           {plan.price}
//         </span>
//         <span
//           className="mb-1 text-xs"
//           style={{ color: plan.popular ? 'rgba(255,255,255,0.65)' : '#94a3b8' }}
//         >
//           {plan.period}
//         </span>
//       </div>
//       <div
//         className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold mb-4"
//         style={{
//           background: plan.popular ? 'rgba(255,255,255,0.15)' : 'rgba(248,114,23,0.07)',
//           color: plan.popular ? '#fff' : '#F87217',
//         }}
//       >
//         <Zap className="w-3 h-3" />
//         {plan.credits}
//       </div>
//       <ul className="space-y-2 mb-5 flex-1">
//         {plan.features.map((f, j) => (
//           <li key={j} className="flex items-center gap-2 text-xs"
//             style={{ color: plan.popular ? 'rgba(255,255,255,0.85)' : '#475569' }}>
//             <div
//               className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0"
//               style={{ background: plan.popular ? 'rgba(255,255,255,0.2)' : 'rgba(248,114,23,0.1)' }}
//             >
//               <Check className="w-2 h-2" style={{ color: plan.popular ? '#fff' : '#F87217' }} />
//             </div>
//             {f}
//           </li>
//         ))}
//       </ul>
//       <button
//         onClick={onGetStarted}
//         className="w-full py-2 rounded-xl text-xs font-bold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
//         style={{
//           background: plan.popular ? 'rgba(255,255,255,0.95)' : '#F87217',
//           color: plan.popular ? '#F87217' : '#fff',
//           boxShadow: plan.popular
//             ? '0 4px 16px rgba(0,0,0,0.1)'
//             : '0 6px 20px rgba(248,114,23,0.35)',
//         }}
//         onMouseEnter={e => {
//           if (!plan.popular) e.currentTarget.style.boxShadow = '0 10px 28px rgba(248,114,23,0.45)';
//         }}
//         onMouseLeave={e => {
//           if (!plan.popular) e.currentTarget.style.boxShadow = '0 6px 20px rgba(248,114,23,0.35)';
//         }}
//       >
//         Get Started
//       </button>
//     </div>
//   );
// }

// /* ── Main Landing ───────────────────────────────────── */
// const Landing = () => {
//   const { isAuthenticated, user } = useAuth();
//   const { isDark } = useTheme();
//   const navigate = useNavigate();
//   const [statsRef, statsVisible] = useInView(0.3);
//   const [heroVisible, setHeroVisible] = useState(false);

//   useEffect(() => {
//     const t = setTimeout(() => setHeroVisible(true), 80);
//     return () => clearTimeout(t);
//   }, []);

//   const c30 = useCounter(30, 1600, statsVisible);
//   const c15 = useCounter(15, 1600, statsVisible);
//   const c999 = useCounter(99, 2000, statsVisible);
//   const c24 = useCounter(24, 1200, statsVisible);

//   const landingRightContent = isAuthenticated ? (
//     <Button
//       onClick={() => navigate('/dashboard')}
//       className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-xl shadow-lg"
//       style={{ boxShadow: '0 4px 16px rgba(248,114,23,0.3)' }}
//       data-testid="dashboard-btn"
//     >
//       Dashboard
//     </Button>
//   ) : (
//     <>
//       <Button
//         variant="ghost"
//         onClick={() => navigate('/login')}
//         className="text-slate-600 hover:text-slate-900 font-semibold rounded-xl"
//         data-testid="login-btn"
//       >
//         Sign In
//       </Button>
//       <Button
//         onClick={() => navigate('/register')}
//         className="text-white rounded-xl font-semibold"
//         style={{
//           background: 'linear-gradient(135deg,#F87217,#FF9A4E)',
//           boxShadow: '0 4px 16px rgba(248,114,23,0.35)',
//         }}
//         data-testid="get-started-btn"
//       >
//         Get Started
//       </Button>
//     </>
//   );

//   return (
//     <div className="min-h-screen overflow-x-hidden" style={{ background: isDark ? '#0a0f1e' : '#fff' }}>
//       <AppHeader rightContent={landingRightContent} />

//       {/* ── Hero Section ─────────────────────────────────────────── */}
//       <section
//         className="relative pt-10 pb-0 overflow-hidden"
//         style={{
//           background: isDark
//             ? 'linear-gradient(160deg,#0a0f1e 0%,#0d1a3a 55%,#0a0f1e 100%)'
//             : 'linear-gradient(160deg,#fffaf6 0%,#ffffff 55%,#f5f3ff 100%)',
//         }}
//       >
//         {/* Animated ambient blobs */}
//         <div
//           className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full pointer-events-none animate-blob"
//           style={{
//             background: isDark
//               ? 'radial-gradient(circle,rgba(248,114,23,0.08) 0%,transparent 65%)'
//               : 'radial-gradient(circle,rgba(248,114,23,0.12) 0%,transparent 65%)',
//           }}
//         />
//         <div
//           className="absolute top-24 -right-32 w-[600px] h-[600px] rounded-full pointer-events-none animate-blob delay-300"
//           style={{
//             background: 'radial-gradient(circle,rgba(139,92,246,0.07) 0%,transparent 65%)',
//             animationDelay: '3s',
//           }}
//         />
//         {/* Subtle grid pattern */}
//         <div
//           className="absolute inset-0 pointer-events-none"
//           style={{
//             backgroundImage: isDark
//               ? 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)'
//               : 'linear-gradient(rgba(0,0,0,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.025) 1px, transparent 1px)',
//             backgroundSize: '48px 48px',
//           }}
//         />

//         <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
//           {/* Left — hero copy */}
//           <div className="relative z-10 py-12 md:py-16 lg:py-20">
//             {/* Badge */}
//             <div
//               className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 text-sm font-semibold"
//               style={{
//                 background: 'rgba(248,114,23,0.08)',
//                 border: '1px solid rgba(248,114,23,0.18)',
//                 color: '#F87217',
//                 opacity: heroVisible ? 1 : 0,
//                 transform: heroVisible ? 'translateY(0)' : 'translateY(12px)',
//                 transition: 'opacity 0.5s ease, transform 0.5s ease',
//               }}
//             >
//               <Sparkles className="w-4 h-4" />
//               30+ Specialized AI Agents
//             </div>

//             <h1
//               className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.2rem] font-extrabold leading-[1.07] mb-5 md:mb-7"
//               style={{
//                 color: isDark ? '#f0f6fc' : '#0d1a3a',
//                 fontFamily: "'Outfit', sans-serif",
//                 opacity: heroVisible ? 1 : 0,
//                 transform: heroVisible ? 'translateY(0)' : 'translateY(18px)',
//                 transition: 'opacity 0.55s ease 0.08s, transform 0.55s ease 0.08s',
//               }}
//             >
//               AI Agents for<br />
//               <span
//                 style={{
//                   background: 'linear-gradient(90deg, #F87217 0%, #FF9A4E 50%, #F87217 100%)',
//                   backgroundSize: '200% auto',
//                   WebkitBackgroundClip: 'text',
//                   WebkitTextFillColor: 'transparent',
//                   backgroundClip: 'text',
//                   animation: 'shimmer 3s linear infinite',
//                 }}
//               >
//                 Every Industry
//               </span>
//             </h1>

//             <p
//               className="text-base md:text-lg mb-7 md:mb-9 max-w-lg leading-relaxed"
//               style={{
//                 color: isDark ? '#8b949e' : '#64748b',
//                 opacity: heroVisible ? 1 : 0,
//                 transform: heroVisible ? 'translateY(0)' : 'translateY(16px)',
//                 transition: 'opacity 0.5s ease 0.16s, transform 0.5s ease 0.16s',
//               }}
//             >
//               Deploy enterprise-grade AI agents across Banking, Healthcare, Legal,
//               Manufacturing, and more. 
//             </p>

//             {/* CTAs */}
//             <div
//               className="flex flex-wrap gap-3.5 mb-10"
//               style={{
//                 opacity: heroVisible ? 1 : 0,
//                 transform: heroVisible ? 'translateY(0)' : 'translateY(14px)',
//                 transition: 'opacity 0.5s ease 0.24s, transform 0.5s ease 0.24s',
//               }}
//             >
//               <button
//                 onClick={() => navigate('/marketplace')}
//                 className="group flex items-center gap-2 px-7 py-3.5 rounded-xl text-white font-bold text-base transition-all duration-200"
//                 style={{
//                   background: 'linear-gradient(135deg,#F87217,#FF9A4E)',
//                   boxShadow: '0 8px 24px rgba(248,114,23,0.35)',
//                 }}
//                 onMouseEnter={e => {
//                   e.currentTarget.style.boxShadow = '0 12px 36px rgba(248,114,23,0.5)';
//                   e.currentTarget.style.transform = 'translateY(-2px)';
//                 }}
//                 onMouseLeave={e => {
//                   e.currentTarget.style.boxShadow = '0 8px 24px rgba(248,114,23,0.35)';
//                   e.currentTarget.style.transform = 'translateY(0)';
//                 }}
//                 data-testid="explore-agents-btn"
//               >
//                 Explore Agents
//                 <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
//               </button>
//               <button
//                 onClick={() => navigate('/pricing')}
//                 className="group flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-base border transition-all duration-200"
//                 style={{
//                   background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.7)',
//                   border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.1)',
//                   color: isDark ? 'rgba(255,255,255,0.85)' : '#1e293b',
//                   backdropFilter: 'blur(8px)',
//                 }}
//                 onMouseEnter={e => {
//                   e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.1)' : '#fff';
//                   e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
//                   e.currentTarget.style.transform = 'translateY(-1px)';
//                 }}
//                 onMouseLeave={e => {
//                   e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.7)';
//                   e.currentTarget.style.boxShadow = 'none';
//                   e.currentTarget.style.transform = 'translateY(0)';
//                 }}
//                 data-testid="view-pricing-btn"
//               >
//                 View Pricing
//                 <ChevronRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
//               </button>
//             </div>

//             {/* Trust badges */}
//             <div
//               className="flex flex-wrap items-center gap-5 text-sm"
//               style={{
//                 color: isDark ? '#6e7681' : '#94a3b8',
//                 opacity: heroVisible ? 1 : 0,
//                 transition: 'opacity 0.5s ease 0.32s',
//               }}
//             >
//               {['No credit card required', '100 free credits', 'SOC2 Compliant'].map((t, i) => (
//                 <span key={i} className="flex items-center gap-1.5">
//                   <Check className="w-3.5 h-3.5 text-emerald-500" />
//                   {t}
//                 </span>
//               ))}
//             </div>
//           </div>

//           {/* Right — hero image  */}
//           <div
//             className="relative hidden md:block"
//             style={{
//               opacity: heroVisible ? 1 : 0,
//               transform: heroVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.97)',
//               transition: 'opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s',
//             }}
//           >
//             <div className="relative" style={{ height: '360px' }}>
//               <HeroOrb style={{ width: '100%', height: '100%', minHeight: '360px' }} />
//             </div>

//             {/* Floating badge 1 */}
//             <div
//               className="absolute -left-8 top-1/4 rounded-2xl px-4 py-3 flex items-center gap-3 animate-float"
//               style={{
//                 background: isDark ? '#0f1729' : '#fff',
//                 boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.6)' : '0 12px 32px rgba(0,0,0,0.12)',
//                 border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.05)',
//               }}
//             >
//               <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-emerald-50">
//                 <TrendingUp className="w-4 h-4 text-emerald-600" />
//               </div>
//               <div>
//                 <p className="text-xs font-bold" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>+34%</p>
//                 <p className="text-[10px]" style={{ color: isDark ? '#6e7681' : '#94a3b8' }}>Efficiency</p>
//               </div>
//             </div>

//             {/* Floating badge 2 */}
//             <div
//               className="absolute -right-5 bottom-1/4 rounded-2xl px-4 py-3 flex items-center gap-3 animate-float delay-500"
//               style={{
//                 background: isDark ? '#0f1729' : '#fff',
//                 boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.6)' : '0 12px 32px rgba(0,0,0,0.12)',
//                 border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.05)',
//               }}
//             >
//               <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-orange-50">
//                 <Zap className="w-4 h-4 text-[#F87217]" />
//               </div>
//               <div>
//                 <p className="text-xs font-bold" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>99.9%</p>
//                 <p className="text-[10px]" style={{ color: isDark ? '#6e7681' : '#94a3b8' }}>Uptime</p>
//               </div>
//             </div>

//             {/* Floating badge 3 */}
//             <div
//               className="absolute left-4 -bottom-4 rounded-2xl px-4 py-3 flex items-center gap-3 animate-float delay-300"
//               style={{
//                 background: isDark ? '#0f1729' : '#fff',
//                 boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.6)' : '0 12px 32px rgba(0,0,0,0.12)',
//                 border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.05)',
//                 animationDelay: '2s',
//               }}
//             >
//               <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-violet-50">
//                 <Brain className="w-4 h-4 text-violet-600" />
//               </div>
//               <div>
//                 <p className="text-xs font-bold" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>30+ Agents</p>
//                 <p className="text-[10px]" style={{ color: isDark ? '#6e7681' : '#94a3b8' }}>Ready to deploy</p>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Compressed Wave Divider */}
//         <div className="relative mt-4">
//           <svg viewBox="0 0 1440 40" xmlns="http://www.w3.org/2000/svg" className="w-full h-8 block">
//             <path
//               d="M0 40 L0 25 Q360 5 720 20 Q1080 35 1440 20 L1440 40 Z"
//               fill={isDark ? '#0f1729' : '#f8fafc'}
//             />
//           </svg>
//         </div>
//       </section>

//       {/* ── Compressed Stats Section ─────────────────────────────────────────── */}
//       <section className="py-8 md:py-10 px-4 sm:px-6" style={{ background: isDark ? '#0f1729' : '#f8fafc' }} ref={statsRef}>
//         <div className="max-w-6xl mx-auto">
//           <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
//             {[
//               { value: c30, suffix: '+', label: 'AI Agents', sub: 'Across all industries' },
//               { value: c15, suffix: '+', label: 'Industries Served', sub: 'Banking to Healthcare' },
//               { value: c999, suffix: '.9%', label: 'Platform Uptime', sub: 'Guaranteed reliability' },
//               { value: c24, suffix: '/7', label: 'Expert Support', sub: 'Always there for you' },
//             ].map((s, i) => (
//               <div key={i} className="flex flex-col items-center text-center group cursor-default py-2">
//                 <p
//                   className="text-3xl md:text-4xl font-extrabold transition-all duration-300 group-hover:scale-105"
//                   style={{
//                     fontFamily: "'Outfit', sans-serif",
//                     background: 'linear-gradient(135deg,#F87217,#FF9A4E)',
//                     WebkitBackgroundClip: 'text',
//                     WebkitTextFillColor: 'transparent',
//                     backgroundClip: 'text',
//                     animation: statsVisible ? `count-up 0.6s ease ${i * 0.12}s both` : 'none',
//                   }}
//                 >
//                   {s.value}{s.suffix}
//                 </p>

//                 <p className="font-semibold mt-1 text-xs" style={{ color: isDark ? '#c9d1d9' : '#334155' }}>
//                   {s.label}
//                 </p>

//                 <p className="text-[11px] mt-0.5" style={{ color: isDark ? '#6e7681' : '#94a3b8' }}>
//                   {s.sub}
//                 </p>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* ── Section divider */}
//       <div className="section-divider" />

//       {/* ── Section divider */}
//       <div className="section-divider" />

//       {/* ── Compressed Features Section ──────────────────────────────────────── */}
//       <section className="py-12 md:py-16 px-4 sm:px-6" style={{ background: isDark ? '#0a0f1e' : '#fff' }}>
//         <div className="max-w-6xl mx-auto">
//           <div className="text-center mb-10">
//             <span className="inline-block text-[10px] font-bold text-[#F87217] uppercase tracking-[0.18em] mb-3 px-3 py-1 rounded-full bg-orange-50 border border-orange-100">
//               Why Choose Us
//             </span>

//             <h2
//               className="text-2xl md:text-3xl font-bold mb-3"
//               style={{ fontFamily: "'Outfit', sans-serif", color: isDark ? '#f0f6fc' : '#0d1a3a' }}
//             >
//               Built for Enterprise.
//               <br className="hidden md:block" />
//               Ready Today.
//             </h2>

//             <p className="max-w-lg mx-auto text-sm leading-relaxed" style={{ color: isDark ? '#8b949e' : '#64748b' }}>
//               Enterprise-grade security, observability, and integrations —
//               deploy with confidence from day one.
//             </p>
//           </div>

//           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
//             {FEATURES.map((f, i) => (
//               <FeatureCard key={i} feature={f} index={i} isDark={isDark} />
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* ── Compressed Dark CTA Section ──────────────────────────────────────── */}
//       <section
//         className="py-12 md:py-16 px-4 sm:px-6 relative overflow-hidden noise-texture"
//         style={{ background: 'linear-gradient(145deg,#0a0a0f 0%,#0d1a3a 60%,#0a0a0f 100%)' }}
//       >
//         {/* Ambient glows */}
//         <div
//           className="absolute inset-0 pointer-events-none"
//           style={{
//             background: 'radial-gradient(ellipse at 50% 110%, rgba(248,114,23,0.18) 0%, transparent 55%)',
//           }}
//         />
//         <div
//           className="absolute top-0 left-1/4 w-72 h-72 rounded-full pointer-events-none"
//           style={{
//             background: 'radial-gradient(circle,rgba(248,114,23,0.05) 0%,transparent 65%)',
//           }}
//         />

//         <div className="max-w-3xl mx-auto text-center relative z-10">
//           {/* Smaller glowing icon */}
//           <div
//             className="mx-auto mb-6 rounded-2xl flex items-center justify-center"
//             style={{
//               background: 'rgba(248,114,23,0.12)',
//               border: '1px solid rgba(248,114,23,0.25)',
//               width: 56,
//               height: 56,
//               boxShadow: '0 0 30px rgba(248,114,23,0.18)',
//             }}
//           >
//             <Globe className="w-6 h-6 text-[#F87217]" />
//           </div>

//           <h2
//             className="text-3xl md:text-4xl font-extrabold text-white mb-4 leading-tight"
//             style={{ fontFamily: "'Outfit', sans-serif" }}
//           >
//             Ready to Transform Your Business?
//           </h2>

//           <p className="text-slate-400 mb-8 max-w-lg mx-auto text-base leading-relaxed">
//             Automate, optimise, and scale faster with Netsmartz AI agents.
//           </p>

//           <div className="flex flex-wrap gap-3 justify-center">
//             <button
//               onClick={() => isAuthenticated ? navigate('/pricing') : navigate('/register')}
//               className="group flex items-center gap-2 px-7 py-3 rounded-lg text-white font-semibold text-sm transition-all duration-200"
//               style={{
//                 background: 'linear-gradient(135deg,#F87217,#FF9A4E)',
//                 boxShadow: '0 8px 24px rgba(248,114,23,0.35)',
//               }}
//               onMouseEnter={e => {
//                 e.currentTarget.style.boxShadow = '0 12px 32px rgba(248,114,23,0.5)';
//                 e.currentTarget.style.transform = 'translateY(-2px)';
//               }}
//               onMouseLeave={e => {
//                 e.currentTarget.style.boxShadow = '0 8px 24px rgba(248,114,23,0.35)';
//                 e.currentTarget.style.transform = 'translateY(0)';
//               }}
//               data-testid="cta-get-started"
//             >
//               {isAuthenticated ? (user?.plan === 'enterprise_pro' ? 'View Marketplace' : 'Upgrade Your Plan') : 'Start Free Trial'}
//               <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
//             </button>

//             <button
//               onClick={() => navigate('/marketplace')}
//               className="flex items-center gap-2 px-7 py-3 rounded-lg font-semibold text-sm transition-all duration-200"
//               style={{
//                 background: 'rgba(255,255,255,0.06)',
//                 border: '1px solid rgba(255,255,255,0.12)',
//                 color: 'rgba(255,255,255,0.8)',
//               }}
//               onMouseEnter={e => {
//                 e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
//                 e.currentTarget.style.color = '#fff';
//                 e.currentTarget.style.transform = 'translateY(-1px)';
//               }}
//               onMouseLeave={e => {
//                 e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
//                 e.currentTarget.style.color = 'rgba(255,255,255,0.8)';
//                 e.currentTarget.style.transform = 'translateY(0)';
//               }}
//             >
//               Browse Agents
//             </button>
//           </div>

//           {/* Smaller security badges */}
//           <div className="mt-8 flex flex-wrap items-center justify-center gap-5">
//             {['SOC2 Certified', 'GDPR Ready', 'HIPAA Compliant', 'AES-256 Encrypted'].map((badge, i) => (
//               <div key={i} className="flex items-center gap-2 text-xs text-slate-500">
//                 <div className="w-4 h-4 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
//                   <Check className="w-3 h-3 text-[#F87217]" />
//                 </div>
//                 {badge}
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>
//       <Footer />
//     </div>
//   );
// };

// export default Landing;
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  Cpu, Zap, ShieldCheck, Users, TrendingUp, BarChart3,
  ArrowRight, Check, Globe, Activity, Sparkles,
  Brain, Lock, Layers, ChevronRight, Star, ClipboardList
} from 'lucide-react';
import HeroOrb from '../components/HeroOrb';
import Footer from '../components/Footer';
import AppHeader from '../components/AppHeader';

/* ── Animated counter hook ─────────────────────────── */
function useCounter(target, duration = 1800, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return value;
}

/* ── Intersection Observer hook ─────────────────────── */
function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const AGENTS = [
  { name: 'Banking Risk & Compliance', icon: ShieldCheck, category: 'Banking & Finance', accentColor: '#3B82F6' },
  { name: 'Customer Support', icon: Users, category: 'Customer Experience', accentColor: '#8B5CF6' },
  { name: 'Sales', icon: TrendingUp, category: 'Sales', accentColor: '#10B981' },
  { name: 'HR Resume Screening', icon: Users, category: 'Human Resources', accentColor: '#EC4899' },
  { name: 'BI Data Storyteller', icon: BarChart3, category: 'Analytics', accentColor: '#F59E0B' },
  { name: 'Clinical Care', icon: Activity, category: 'Healthcare', accentColor: '#14B8A6' },
];

const FEATURES = [
  {
    icon: Brain,
    title: 'ReAct Intelligence',
    desc: 'Agents reason step-by-step, call tools, observe results — delivering accurate, auditable answers every time.',
    accentColor: '#F87217',
  },
  {
    icon: Lock,
    title: 'Enterprise Security',
    desc: 'SOC2, GDPR, HIPAA-ready. AES-256 encryption at rest and in transit. Your data never leaves your environment.',
    accentColor: '#3B82F6',
  },
  {
    icon: Layers,
    title: 'Plug & Play Integration',
    desc: 'Connect to Salesforce, Dynamics 365, Azure, Twilio, and more with zero custom code.',
    accentColor: '#8B5CF6',
  },
  {
    icon: Zap,
    title: 'Credit-Based Scale',
    desc: 'Pay only for what you use. Start free, scale seamlessly from solo to enterprise-grade workloads.',
    accentColor: '#10B981',
  },
];

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: '/month',
    credits: '100 credits',
    features: ['Access to Free-tier agents', 'Basic support', 'Community access', '20 MB Knowledge Base Storage'],
    popular: false,
  },
  {
    name: 'Pro',
    price: '$49',
    period: '/month',
    credits: '5,000 credits',
    features: ['All Pro agents', 'Priority support', 'API access', 'Custom integrations', '1 GB Knowledge Base Storage'],
    popular: true,
  },
  {
    name: 'Enterprise',
    price: '$299',
    period: '/month',
    credits: '50,000+ credits',
    features: ['All agents', 'Dedicated support', 'SLA guarantee', 'On-premise option', '2 GB Knowledge Base Storage'],
    popular: false,
  },
  {
    name: 'Enterprise Pro',
    price: '$499',
    period: '/month',
    credits: '80,000 credits',
    features: ['Access to all agents', 'Dedicated account manager + onboarding', 'Advanced analytics', '5GB Knowledge Base Storage'],
    popular: false,
  },
];

/* ── Feature Card ───────────────────────────────────── */
function FeatureCard({ feature, index, isDark }) {
  const [ref, inView] = useInView(0.15);
  const cardBg = isDark ? '#0f1729' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
  const cardShadow = isDark ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.04)';
  return (
    <div
      ref={ref}
      className="group relative p-7 rounded-2xl border transition-all duration-300 cursor-default"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.5s ease ${index * 0.1}s, transform 0.5s ease ${index * 0.1}s, box-shadow 0.25s ease, border-color 0.25s ease`,
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        boxShadow: cardShadow,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = feature.accentColor + '55';
        e.currentTarget.style.boxShadow = `0 12px 40px ${feature.accentColor}18, 0 4px 12px rgba(0,0,0,0.06)`;
        e.currentTarget.style.transform = 'translateY(-4px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = cardBorder;
        e.currentTarget.style.boxShadow = cardShadow;
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div
        className="absolute top-0 left-6 right-6 h-[2px] rounded-b-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: `linear-gradient(90deg,${feature.accentColor},${feature.accentColor}88)` }}
      />
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110"
        style={{ background: feature.accentColor + '12', border: `1px solid ${feature.accentColor}22` }}
      >
        <feature.icon className="w-5 h-5" style={{ color: feature.accentColor }} />
      </div>
      <h3 className="font-bold mb-2.5 text-base" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>{feature.title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: isDark ? '#8b949e' : '#64748b' }}>{feature.desc}</p>
    </div>
  );
}

/* ── Agent Card ─────────────────────────────────────── */
function AgentCard({ agent, index, onClick, isDark }) {
  const [ref, inView] = useInView(0.1);
  const cardBg = isDark ? '#0f1729' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(226,232,240,1)';
  return (
    <div
      ref={ref}
      className="group relative border rounded-2xl p-5 flex items-center gap-4 cursor-pointer"
      onClick={onClick}
      style={{
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.45s ease ${index * 0.08}s, transform 0.45s ease ${index * 0.08}s, box-shadow 0.25s ease, border-color 0.25s ease`,
        boxShadow: isDark ? '0 1px 4px rgba(0,0,0,0.4)' : '0 1px 4px rgba(0,0,0,0.05)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = '#F8721733';
        e.currentTarget.style.boxShadow = '0 8px 28px rgba(248,114,23,0.12), 0 2px 8px rgba(0,0,0,0.05)';
        e.currentTarget.style.transform = 'translateY(-3px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = cardBorder;
        e.currentTarget.style.boxShadow = isDark ? '0 1px 4px rgba(0,0,0,0.4)' : '0 1px 4px rgba(0,0,0,0.05)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
        style={{ background: agent.accentColor + '14', border: `1px solid ${agent.accentColor}22` }}
      >
        <agent.icon className="w-5 h-5" style={{ color: agent.accentColor }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color: isDark ? '#586069' : '#94a3b8' }}>{agent.category}</p>
        <h3 className="font-semibold text-sm truncate group-hover:text-[#F87217] transition-colors duration-200" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>{agent.name}</h3>
      </div>
      <ArrowRight
        className="w-4 h-4 flex-shrink-0 transition-all duration-200 group-hover:translate-x-1"
        style={{ color: '#F87217', opacity: 0.5 }}
      />
    </div>
  );
}

/* ── Pricing Card ───────────────────────────────────── */
function PricingCard({ plan, index, onGetStarted }) {
  const [ref, inView] = useInView(0.1);
  return (
    <div
      ref={ref}
      className="relative rounded-2xl p-5 flex flex-col"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.5s ease ${index * 0.1}s, transform 0.5s ease ${index * 0.1}s`,
        background: plan.popular
          ? 'linear-gradient(145deg,#F87217 0%,#FF9A4E 100%)'
          : '#fff',
        border: plan.popular ? 'none' : '1px solid rgba(0,0,0,0.07)',
        boxShadow: plan.popular
          ? '0 24px 64px rgba(248,114,23,0.35), 0 8px 24px rgba(248,114,23,0.2)'
          : '0 2px 8px rgba(0,0,0,0.04)',
      }}
    >
      {plan.popular && (
        <div
          className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-5 py-1.5 text-xs font-bold rounded-full flex items-center gap-1.5"
          style={{ background: '#0d1a3a', color: '#fff' }}
        >
          <Star className="w-3 h-3 fill-[#F87217] stroke-none" />
          MOST POPULAR
        </div>
      )}
      <h3
        className="text-sm font-bold mb-1"
        style={{ color: plan.popular ? 'rgba(255,255,255,0.9)' : '#0d1a3a' }}
      >
        {plan.name}
      </h3>
      <div className="flex items-end gap-1 mt-2 mb-1.5">
        <span
          className="text-2xl font-extrabold tracking-tight"
          style={{ color: plan.popular ? '#fff' : '#0d1a3a' }}
        >
          {plan.price}
        </span>
        <span
          className="mb-1 text-xs"
          style={{ color: plan.popular ? 'rgba(255,255,255,0.65)' : '#94a3b8' }}
        >
          {plan.period}
        </span>
      </div>
      <div
        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold mb-4"
        style={{
          background: plan.popular ? 'rgba(255,255,255,0.15)' : 'rgba(248,114,23,0.07)',
          color: plan.popular ? '#fff' : '#F87217',
        }}
      >
        <Zap className="w-3 h-3" />
        {plan.credits}
      </div>
      <ul className="space-y-2 mb-5 flex-1">
        {plan.features.map((f, j) => (
          <li key={j} className="flex items-center gap-2 text-xs"
            style={{ color: plan.popular ? 'rgba(255,255,255,0.85)' : '#475569' }}>
            <div
              className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: plan.popular ? 'rgba(255,255,255,0.2)' : 'rgba(248,114,23,0.1)' }}
            >
              <Check className="w-2 h-2" style={{ color: plan.popular ? '#fff' : '#F87217' }} />
            </div>
            {f}
          </li>
        ))}
      </ul>
      <button
        onClick={onGetStarted}
        className="w-full py-2 rounded-xl text-xs font-bold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
        style={{
          background: plan.popular ? 'rgba(255,255,255,0.95)' : '#F87217',
          color: plan.popular ? '#F87217' : '#fff',
          boxShadow: plan.popular
            ? '0 4px 16px rgba(0,0,0,0.1)'
            : '0 6px 20px rgba(248,114,23,0.35)',
        }}
        onMouseEnter={e => {
          if (!plan.popular) e.currentTarget.style.boxShadow = '0 10px 28px rgba(248,114,23,0.45)';
        }}
        onMouseLeave={e => {
          if (!plan.popular) e.currentTarget.style.boxShadow = '0 6px 20px rgba(248,114,23,0.35)';
        }}
      >
        Get Started
      </button>
    </div>
  );
}

/* ── Main Landing ───────────────────────────────────── */
const Landing = () => {
  const { isAuthenticated, user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [statsRef, statsVisible] = useInView(0.3);
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const c30 = useCounter(30, 1600, statsVisible);
  const c15 = useCounter(15, 1600, statsVisible);
  const c999 = useCounter(99, 2000, statsVisible);
  const c24 = useCounter(24, 1200, statsVisible);

  const landingRightContent = isAuthenticated ? (
    <Button
      onClick={() => navigate('/dashboard')}
      className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-xl shadow-lg"
      style={{ boxShadow: '0 4px 16px rgba(248,114,23,0.3)' }}
      data-testid="dashboard-btn"
    >
      Dashboard
    </Button>
  ) : (
    <>
      <Button
        variant="ghost"
        onClick={() => navigate('/login')}
        className="text-slate-600 hover:text-slate-900 font-semibold rounded-xl"
        data-testid="login-btn"
      >
        Sign In
      </Button>
      <Button
        onClick={() => navigate('/register')}
        className="text-white rounded-xl font-semibold"
        style={{
          background: 'linear-gradient(135deg,#F87217,#FF9A4E)',
          boxShadow: '0 4px 16px rgba(248,114,23,0.35)',
        }}
        data-testid="get-started-btn"
      >
        Get Started
      </Button>
    </>
  );

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: isDark ? '#0a0f1e' : '#fff' }}>
      <AppHeader rightContent={landingRightContent} />

      {/* ── Hero Section ─────────────────────────────────────────── */}
      <section
        className="relative pt-10 pb-0 overflow-hidden"
        style={{
          background: isDark
            ? 'linear-gradient(160deg,#0a0f1e 0%,#0d1a3a 55%,#0a0f1e 100%)'
            : 'linear-gradient(160deg,#fffaf6 0%,#ffffff 55%,#f5f3ff 100%)',
        }}
      >
        {/* Animated ambient blobs */}
        <div
          className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full pointer-events-none animate-blob"
          style={{
            background: isDark
              ? 'radial-gradient(circle,rgba(248,114,23,0.08) 0%,transparent 65%)'
              : 'radial-gradient(circle,rgba(248,114,23,0.12) 0%,transparent 65%)',
          }}
        />
        <div
          className="absolute top-24 -right-32 w-[600px] h-[600px] rounded-full pointer-events-none animate-blob delay-300"
          style={{
            background: 'radial-gradient(circle,rgba(139,92,246,0.07) 0%,transparent 65%)',
            animationDelay: '3s',
          }}
        />
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: isDark
              ? 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)'
              : 'linear-gradient(rgba(0,0,0,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.025) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left — hero copy */}
          <div className="relative z-10 py-12 md:py-16 lg:py-20">
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 text-sm font-semibold"
              style={{
                background: 'rgba(248,114,23,0.08)',
                border: '1px solid rgba(248,114,23,0.18)',
                color: '#F87217',
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? 'translateY(0)' : 'translateY(12px)',
                transition: 'opacity 0.5s ease, transform 0.5s ease',
              }}
            >
              <Sparkles className="w-4 h-4" />
              30+ Specialized AI Agents
            </div>

            <h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.2rem] font-extrabold leading-[1.07] mb-5 md:mb-7"
              style={{
                color: isDark ? '#f0f6fc' : '#0d1a3a',
                fontFamily: "'Outfit', sans-serif",
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? 'translateY(0)' : 'translateY(18px)',
                transition: 'opacity 0.55s ease 0.08s, transform 0.55s ease 0.08s',
              }}
            >
              AI Agents for<br />
              <span
                style={{
                  background: 'linear-gradient(90deg, #F87217 0%, #FF9A4E 50%, #F87217 100%)',
                  backgroundSize: '200% auto',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'shimmer 3s linear infinite',
                }}
              >
                Every Industry
              </span>
            </h1>

            <p
              className="text-base md:text-lg mb-7 md:mb-9 max-w-lg leading-relaxed"
              style={{
                color: isDark ? '#8b949e' : '#64748b',
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? 'translateY(0)' : 'translateY(16px)',
                transition: 'opacity 0.5s ease 0.16s, transform 0.5s ease 0.16s',
              }}
            >
              Deploy enterprise-grade AI agents across Banking, Healthcare, Legal,
              Manufacturing, and more.
            </p>

            {/* ── CTAs ── */}
            <div
              className="flex flex-col gap-3 mb-10 w-fit" // Added w-fit here
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? 'translateY(0)' : 'translateY(14px)',
                transition: 'opacity 0.5s ease 0.24s, transform 0.5s ease 0.24s',
              }}
            >
              {/* Row 1: Explore Agents + View Pricing side by side */}
              <div className="flex flex-nowrap gap-3.5" id="cta-row-1"> {/* Changed flex-wrap to flex-nowrap to keep them together */}
                {/* Explore Agents — primary orange */}
                <button
                  onClick={() => navigate('/marketplace')}
                  className="group flex items-center gap-2 px-7 py-3.5 rounded-xl text-white font-bold text-base transition-all duration-200 whitespace-nowrap"
                  style={{
                    background: 'linear-gradient(135deg,#F87217,#FF9A4E)',
                    boxShadow: '0 8px 24px rgba(248,114,23,0.35)',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.boxShadow = '0 12px 36px rgba(248,114,23,0.5)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(248,114,23,0.35)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  data-testid="explore-agents-btn"
                >
                  Explore Agents
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                </button>

                {/* View Pricing — ghost */}
                <button
                  onClick={() => navigate('/pricing')}
                  className="group flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-base border transition-all duration-200 whitespace-nowrap"
                  style={{
                    background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.7)',
                    border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.1)',
                    color: isDark ? 'rgba(255,255,255,0.85)' : '#1e293b',
                    backdropFilter: 'blur(8px)',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.1)' : '#fff';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.7)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  data-testid="view-pricing-btn"
                >
                  View Pricing
                  <ChevronRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </button>
              </div>

              {/* Row 2: Check AI Readiness — matches combined width of row 1 */}
              <button
                onClick={() => navigate('/ai-readiness')}
                className="group flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-base border transition-all duration-200 w-full" // Added w-full here
                style={{
                  background: 'linear-gradient(135deg,#F87217,#FF9A4E)',
                  boxShadow: '0 8px 24px rgba(248,114,23,0.35)',
                  color: 'white', // Added text color for readability
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.boxShadow = '0 12px 36px rgba(248,114,23,0.5)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(248,114,23,0.35)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                data-testid="check-ai-readiness-btn"
              >
                <ClipboardList className="w-4 h-4" />
                Check AI Readiness
                <ChevronRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
            </div>

            {/* Trust badges */}
            <div
              className="flex flex-wrap items-center gap-5 text-sm"
              style={{
                color: isDark ? '#6e7681' : '#94a3b8',
                opacity: heroVisible ? 1 : 0,
                transition: 'opacity 0.5s ease 0.32s',
              }}
            >
              {['No credit card required', '100 free credits', 'SOC2 Compliant'].map((t, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-500" />
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Right — hero image */}
          <div
            className="relative hidden md:block"
            style={{
              opacity: heroVisible ? 1 : 0,
              transform: heroVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.97)',
              transition: 'opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s',
            }}
          >
            <div className="relative" style={{ height: '360px' }}>
              <HeroOrb style={{ width: '100%', height: '100%', minHeight: '360px' }} />
            </div>

            {/* Floating badge 1 */}
            <div
              className="absolute -left-8 top-1/4 rounded-2xl px-4 py-3 flex items-center gap-3 animate-float"
              style={{
                background: isDark ? '#0f1729' : '#fff',
                boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.6)' : '0 12px 32px rgba(0,0,0,0.12)',
                border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-emerald-50">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs font-bold" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>+34%</p>
                <p className="text-[10px]" style={{ color: isDark ? '#6e7681' : '#94a3b8' }}>Efficiency</p>
              </div>
            </div>

            {/* Floating badge 2 */}
            <div
              className="absolute -right-5 bottom-1/4 rounded-2xl px-4 py-3 flex items-center gap-3 animate-float delay-500"
              style={{
                background: isDark ? '#0f1729' : '#fff',
                boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.6)' : '0 12px 32px rgba(0,0,0,0.12)',
                border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-orange-50">
                <Zap className="w-4 h-4 text-[#F87217]" />
              </div>
              <div>
                <p className="text-xs font-bold" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>99.9%</p>
                <p className="text-[10px]" style={{ color: isDark ? '#6e7681' : '#94a3b8' }}>Uptime</p>
              </div>
            </div>

            {/* Floating badge 3 */}
            <div
              className="absolute left-4 -bottom-4 rounded-2xl px-4 py-3 flex items-center gap-3 animate-float delay-300"
              style={{
                background: isDark ? '#0f1729' : '#fff',
                boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.6)' : '0 12px 32px rgba(0,0,0,0.12)',
                border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.05)',
                animationDelay: '2s',
              }}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-violet-50">
                <Brain className="w-4 h-4 text-violet-600" />
              </div>
              <div>
                <p className="text-xs font-bold" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>30+ Agents</p>
                <p className="text-[10px]" style={{ color: isDark ? '#6e7681' : '#94a3b8' }}>Ready to deploy</p>
              </div>
            </div>
          </div>
        </div>

        {/* Compressed Wave Divider */}
        <div className="relative mt-4">
          <svg viewBox="0 0 1440 40" xmlns="http://www.w3.org/2000/svg" className="w-full h-8 block">
            <path
              d="M0 40 L0 25 Q360 5 720 20 Q1080 35 1440 20 L1440 40 Z"
              fill={isDark ? '#0f1729' : '#f8fafc'}
            />
          </svg>
        </div>
      </section>

      {/* ── Compressed Stats Section ─────────────────────────────────────────── */}
      <section className="py-8 md:py-10 px-4 sm:px-6" style={{ background: isDark ? '#0f1729' : '#f8fafc' }} ref={statsRef}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { value: c30, suffix: '+', label: 'AI Agents', sub: 'Across all industries' },
              { value: c15, suffix: '+', label: 'Industries Served', sub: 'Banking to Healthcare' },
              { value: c999, suffix: '.9%', label: 'Platform Uptime', sub: 'Guaranteed reliability' },
              { value: c24, suffix: '/7', label: 'Expert Support', sub: 'Always there for you' },
            ].map((s, i) => (
              <div key={i} className="flex flex-col items-center text-center group cursor-default py-2">
                <p
                  className="text-3xl md:text-4xl font-extrabold transition-all duration-300 group-hover:scale-105"
                  style={{
                    fontFamily: "'Outfit', sans-serif",
                    background: 'linear-gradient(135deg,#F87217,#FF9A4E)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    animation: statsVisible ? `count-up 0.6s ease ${i * 0.12}s both` : 'none',
                  }}
                >
                  {s.value}{s.suffix}
                </p>
                <p className="font-semibold mt-1 text-xs" style={{ color: isDark ? '#c9d1d9' : '#334155' }}>
                  {s.label}
                </p>
                <p className="text-[11px] mt-0.5" style={{ color: isDark ? '#6e7681' : '#94a3b8' }}>
                  {s.sub}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />
      <div className="section-divider" />

      {/* ── Compressed Features Section ──────────────────────────────────────── */}
      <section className="py-12 md:py-16 px-4 sm:px-6" style={{ background: isDark ? '#0a0f1e' : '#fff' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-[10px] font-bold text-[#F87217] uppercase tracking-[0.18em] mb-3 px-3 py-1 rounded-full bg-orange-50 border border-orange-100">
              Why Choose Us
            </span>
            <h2
              className="text-2xl md:text-3xl font-bold mb-3"
              style={{ fontFamily: "'Outfit', sans-serif", color: isDark ? '#f0f6fc' : '#0d1a3a' }}
            >
              Built for Enterprise.
              <br className="hidden md:block" />
              Ready Today.
            </h2>
            <p className="max-w-lg mx-auto text-sm leading-relaxed" style={{ color: isDark ? '#8b949e' : '#64748b' }}>
              Enterprise-grade security, observability, and integrations —
              deploy with confidence from day one.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f, i) => (
              <FeatureCard key={i} feature={f} index={i} isDark={isDark} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Compressed Dark CTA Section ──────────────────────────────────────── */}
      <section
        className="py-12 md:py-16 px-4 sm:px-6 relative overflow-hidden noise-texture"
        style={{ background: 'linear-gradient(145deg,#0a0a0f 0%,#0d1a3a 60%,#0a0a0f 100%)' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 110%, rgba(248,114,23,0.18) 0%, transparent 55%)' }}
        />
        <div
          className="absolute top-0 left-1/4 w-72 h-72 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle,rgba(248,114,23,0.05) 0%,transparent 65%)' }}
        />

        <div className="max-w-3xl mx-auto text-center relative z-10">
          <div
            className="mx-auto mb-6 rounded-2xl flex items-center justify-center"
            style={{
              background: 'rgba(248,114,23,0.12)',
              border: '1px solid rgba(248,114,23,0.25)',
              width: 56,
              height: 56,
              boxShadow: '0 0 30px rgba(248,114,23,0.18)',
            }}
          >
            <Globe className="w-6 h-6 text-[#F87217]" />
          </div>

          <h2
            className="text-3xl md:text-4xl font-extrabold text-white mb-4 leading-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Ready to Transform Your Business?
          </h2>

          <p className="text-slate-400 mb-8 max-w-lg mx-auto text-base leading-relaxed">
            Automate, optimise, and scale faster with Netsmartz AI agents.
          </p>

          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => isAuthenticated ? navigate('/pricing') : navigate('/register')}
              className="group flex items-center gap-2 px-7 py-3 rounded-lg text-white font-semibold text-sm transition-all duration-200"
              style={{ background: 'linear-gradient(135deg,#F87217,#FF9A4E)', boxShadow: '0 8px 24px rgba(248,114,23,0.35)' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 12px 32px rgba(248,114,23,0.5)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(248,114,23,0.35)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              data-testid="cta-get-started"
            >
              {isAuthenticated ? (user?.plan === 'enterprise_pro' ? 'View Marketplace' : 'Upgrade Your Plan') : 'Start Free Trial'}
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>

            {/* ── CTA section: Check AI Readiness (secondary) ── */}
            <button
              onClick={() => navigate('/ai-readiness')}
              className="group flex items-center gap-2 px-7 py-3 rounded-lg text-white font-semibold text-sm transition-all duration-200"
              style={{ background: 'linear-gradient(135deg,#F87217,#FF9A4E)', boxShadow: '0 8px 24px rgba(248,114,23,0.35)' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 12px 32px rgba(248,114,23,0.5)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(248,114,23,0.35)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              data-testid="cta-ai-readiness"
            >
              <ClipboardList className="w-4 h-4" />
              Check AI Readiness
            </button>

            <button
              onClick={() => navigate('/marketplace')}
              className="flex items-center gap-2 px-7 py-3 rounded-lg font-semibold text-sm transition-all duration-200"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Browse Agents
            </button>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-5">
            {['SOC2 Certified', 'GDPR Ready', 'HIPAA Compliant', 'AES-256 Encrypted'].map((badge, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-slate-500">
                <div className="w-4 h-4 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-[#F87217]" />
                </div>
                {badge}
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Landing;